import { getLocalStorageItem, setLocalStorageItem } from "./use-local-storage";

/**
 * A localStorage-backed set of string keys, capped so it cannot grow without limit.
 *
 * Several transfer flows need the same thing: remember which items have already been handled for
 * a given wallet (and sometimes chain), survive a reload, and never let a heavy user's ledger
 * grow unbounded. Corrupt or missing data reads as an empty set rather than throwing — losing
 * the ledger costs a re-import, throwing breaks the flow.
 */
export interface BoundedKeySet {
  /** Current members. Empty when unset or corrupt. */
  read: () => Set<string>;
  /** Adds members; once the cap is exceeded the oldest entries drop first. */
  add: (...keys: string[]) => void;
  /** Drops members, so the flow that guards on them can handle the item again. */
  remove: (...keys: string[]) => void;
}

/**
 * Builds a scoped-set factory. `scope` segments are lowercased and joined into the storage key,
 * so callers pass the wallet address (and chain, where the ledger is per-chain).
 */
export function boundedKeySet(prefix: string, max: number): (...scope: string[]) => BoundedKeySet {
  return (...scope: string[]) => {
    const storageKey = [prefix, ...scope.map((segment) => segment.toLowerCase())].join("-");

    const read = (): Set<string> => {
      const raw = getLocalStorageItem(storageKey);
      if (!raw) return new Set();
      try {
        const parsed: unknown = JSON.parse(raw);
        if (Array.isArray(parsed)) return new Set(parsed as string[]);
      } catch {
        /* ignore corrupt data */
      }
      return new Set();
    };

    return {
      read,
      add: (...keys: string[]) => {
        if (keys.length === 0) return;
        const merged = read();
        for (const key of keys) merged.add(key);
        const entries = Array.from(merged);
        setLocalStorageItem(storageKey, entries.length > max ? entries.slice(-max) : entries);
      },
      remove: (...keys: string[]) => {
        if (keys.length === 0) return;
        const remaining = read();
        let removed = false;
        for (const key of keys) removed = remaining.delete(key) || removed;
        // Nothing to write back for a key the ledger never held, which is most calls: the caller
        // asks on every dismissal, not only the ones that were imported.
        if (!removed) return;
        setLocalStorageItem(storageKey, Array.from(remaining));
      },
    };
  };
}
